import { ArrowLeft, Lock } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { useUserProfile } from "@/hooks/useUserProfile";
import { hasPermission } from "@/lib/roles";
import { ExportProfilesTab } from "./export-profiles-tab";
import { GlAccountsTab } from "./gl-accounts-tab";
import { ImportTab } from "./import-tab";
import { PoliciesTab } from "./policies-tab";
import { VendorsTab } from "./vendors-tab";

export default function AccountingBillsSettingsPage() {
  const { authUserId } = useUserProfile();
  const { userRole } = useAuthIdentity();
  const canManage = hasPermission(userRole, "accounting:manage");

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" className="-ml-2 px-2" asChild>
          <Link to="/admin/accounting/bills">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Bills
          </Link>
        </Button>
        <h1 className="text-3xl font-bold tracking-tight">Bills settings</h1>
        <p className="text-muted-foreground mt-1">
          Vendors, GL accounts, QBO export profiles, and optional approval
          automation for Bills &amp; Expenses.
        </p>
      </div>

      {!canManage && (
        <div className="text-muted-foreground flex items-center gap-2 rounded-md border p-3 text-sm">
          <Lock className="h-4 w-4" />
          Read-only — managing bill master data requires accounting management
          access.
        </div>
      )}

      <Tabs defaultValue="vendors">
        <TabsList>
          <TabsTrigger value="vendors">Vendors</TabsTrigger>
          <TabsTrigger value="gl-accounts">GL Accounts</TabsTrigger>
          <TabsTrigger value="export-profiles">Export Profiles</TabsTrigger>
          <TabsTrigger value="policies">Advanced Approval Rules</TabsTrigger>
          <TabsTrigger value="import">Import</TabsTrigger>
        </TabsList>
        <TabsContent value="vendors">
          <VendorsTab authUserId={authUserId ?? undefined} canManage={canManage} />
        </TabsContent>
        <TabsContent value="gl-accounts">
          <GlAccountsTab
            authUserId={authUserId ?? undefined}
            canManage={canManage}
          />
        </TabsContent>
        <TabsContent value="policies">
          <PoliciesTab
            authUserId={authUserId ?? undefined}
            canManage={canManage}
          />
        </TabsContent>
        <TabsContent value="export-profiles">
          <ExportProfilesTab
            authUserId={authUserId ?? undefined}
            canManage={canManage}
          />
        </TabsContent>
        <TabsContent value="import">
          <ImportTab authUserId={authUserId ?? undefined} canManage={canManage} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
